import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { financialSummary } from "@/mocks/financial";
import { computeAppFees } from "@/utils/appFees";
import { formatSAR } from "@/utils/format";
import AppFeesSummary from "./components/AppFeesSummary";
import TransactionsTable from "./components/TransactionsTable";

export default function FinancialPage() {
  const navigate = useNavigate();
  const { cashDebt: debt, transactions: list, orders } = useStore();
  const appFees = useMemo(() => computeAppFees(orders), [orders]);

  const kpis = useMemo(
    () => [
      {
        label: "رصيد المحفظة",
        value: formatSAR(financialSummary.walletBalance),
        icon: "ri-wallet-3-line",
        tone: "bg-primary-100 text-primary-700",
      },
      {
        label: "معلّق للتحويل",
        value: formatSAR(financialSummary.pendingWallet),
        icon: "ri-time-line",
        tone: "bg-accent-100 text-accent-800",
      },
      {
        label: "رسوم التطبيق المستحقة لطلبات الكاش",
        value: formatSAR(debt),
        icon: "ri-hand-coin-line",
        tone: debt > 0 ? "bg-red-100 text-red-600" : "bg-secondary-200 text-secondary-800",
      },
      {
        label: "تسويات هذا الشهر",
        value: formatSAR(financialSummary.settledThisMonth),
        icon: "ri-bank-line",
        tone: "bg-secondary-200 text-secondary-800",
      },
    ],
    [debt],
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((item) => (
          <div
            key={item.label}
            className="yc-fade-up yc-card-hover bg-white rounded-card border border-background-200 shadow-card p-5"
          >
            <div className="flex items-center gap-2 text-foreground-500">
              <span
                className={`w-8 h-8 flex items-center justify-center rounded-full ${item.tone}`}
              >
                <i className={item.icon}></i>
              </span>
              <span className="text-xs">{item.label}</span>
            </div>
            <p className="mt-3 text-2xl font-bold text-foreground-950">
              {item.value}
            </p>
          </div>
        ))}
      </div>

      {debt > 0 ? (
        <section className="yc-fade-up flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between rounded-card border border-red-200 bg-red-50 p-5">
          <div className="flex items-center gap-4">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-red-100 text-red-600 text-xl shrink-0">
              <i className="ri-error-warning-line"></i>
            </span>
            <div>
              <h3 className="text-base font-bold text-red-700">
                حالة الدفع النقدي: معلّق
              </h3>
              <p className="mt-1 text-sm leading-6 text-red-600/90">
                يجب سداد رسوم التطبيق المستحقة لطلبات الكاش ({formatSAR(debt)})
                لإعادة تفعيل استقبال الطلبات النقدية.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/financial/settle")}
            className="inline-flex items-center justify-center gap-2 h-11 rounded-btn bg-red-500 px-6 text-sm font-semibold text-white hover:bg-red-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-hand-coin-line"></i>
            سداد الآن
          </button>
        </section>
      ) : (
        <section className="yc-fade-up flex items-center gap-4 rounded-card border border-primary-200 bg-primary-50 p-5">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xl shrink-0">
            <i className="ri-shield-check-line"></i>
          </span>
          <div>
            <h3 className="text-base font-bold text-primary-800">
              لا توجد رسوم مستحقة
            </h3>
            <p className="mt-1 text-sm text-primary-700/90">
              الدفع النقدي مفعّل وحسابك في وضع سليم.
            </p>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <TransactionsTable transactions={list} />
        </div>

        <div className="space-y-4">
          <AppFeesSummary
            total={appFees.total}
            orders={appFees.orders}
            perOrder={appFees.perOrder}
            electronic={appFees.electronic}
            electronicOrders={appFees.electronicOrders}
            cash={appFees.cash}
            cashOrders={appFees.cashOrders}
          />
        </div>
      </div>
    </div>
  );
}